import { useState } from 'react';
import { useUpdateCryptoSale } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { Field, Modal } from '@/components/ui';
import { LeadPicker } from '@/components/leads/LeadPicker';
import { IconCheck } from '@/components/Icons';
import type { CryptoSale } from '@/lib/types';

interface EditCryptoSaleModalProps {
  sale: CryptoSale;
  onClose: () => void;
}

/** Edita o que não mexe nos valores: o nome, a corretora, a observação e o cliente. */
export function EditCryptoSaleModal({ sale, onClose }: EditCryptoSaleModalProps) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const update = useUpdateCryptoSale();
  const [description, setDescription] = useState(sale.description);
  const [exchange, setExchange] = useState(sale.exchange ?? '');
  const [note, setNote] = useState(sale.note ?? '');
  const [lead, setLead] = useState(sale.lead);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (description.trim().length < 2) return setError('Informe o que foi vendido.');
    setError(null);
    try {
      await update.mutateAsync({
        id: sale.id,
        description: description.trim(),
        exchange: exchange.trim() || null,
        note: note.trim() || null,
        leadId: lead?.id ?? null,
      });
      toast.success('Venda atualizada.');
      onClose();
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  return (
    <Modal
      title="Editar venda em cripto"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={update.isPending}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={update.isPending}>
            {update.isPending ? <span className="spinner" /> : <IconCheck />} Salvar
          </button>
        </>
      }
    >
      <p className="muted" role="note" style={{ fontSize: 12.5, margin: 0 }}>
        Valores e datas das parcelas não mudam aqui. Para corrigir uma parcela paga, desmarque e marque de novo.
      </p>
      <Field label="O que foi vendido *">
        <input className="inp" value={description} onChange={(event) => setDescription(event.target.value)} maxLength={200} autoFocus />
      </Field>
      <Field label="Corretora">
        <input className="inp" value={exchange} onChange={(event) => setExchange(event.target.value)} maxLength={60} />
      </Field>
      <LeadPicker value={lead?.id ?? ''} valueName={lead?.name ?? ''} isAdmin={isAdmin} onChange={(picked) => setLead(picked)} />
      <Field label="Observação">
        <input className="inp" value={note} onChange={(event) => setNote(event.target.value)} maxLength={500} />
      </Field>
      {error ? <div className="alert alert-error" role="alert">{error}</div> : null}
    </Modal>
  );
}
